import type { AxiosInstance } from "axios";

/** Image types the backend signs upload URLs for (profile and workspace images). */
export const SUPPORTED_IMAGE_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/** Same limit the backend checks after upload. */
export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;

interface SignedUploadTarget {
  url: string;
  file_path: string;
  content_type?: string;
}

/** Signed upload endpoints answer `{ data: { url, file_path, ... } }`. */
const readSignedUploadTarget = (body: unknown): SignedUploadTarget => {
  const envelope = body as { data?: SignedUploadTarget } | SignedUploadTarget | null | undefined;
  const target =
    envelope && typeof envelope === "object" && "data" in envelope && envelope.data
      ? envelope.data
      : (envelope as SignedUploadTarget | null | undefined);
  if (!target?.url || !target?.file_path) {
    throw new Error("Upload URL response is missing url or file_path");
  }
  return target;
};

/**
 * PUT a file to a signed Cloud Storage URL. Uses the plain axios instance on purpose:
 * the app instance would attach auth headers the signature does not cover.
 */
export const putFileToSignedUrl = async (
  url: string,
  file: File,
  contentType: string,
  onUploadProgress?: (fraction: number) => void
): Promise<void> => {
  const { default: rawAxios } = await import("axios");
  await rawAxios.put(url, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (event) => {
      if (event.total) {
        onUploadProgress?.(event.loaded / event.total);
      }
    },
  });
};

/**
 * Ask the backend for a signed image upload URL, upload the file to it, and return the
 * storage path to save on the profile or workspace.
 */
export const uploadImageViaSignedUrl = async (
  axios: AxiosInstance,
  uploadUrlEndpoint: string,
  file: File
): Promise<string> => {
  const body = await axios.get(uploadUrlEndpoint, { params: { content_type: file.type } });
  const { url, file_path, content_type } = readSignedUploadTarget(body);
  await putFileToSignedUrl(url, file, content_type ?? file.type);
  return file_path;
};

/**
 * Ask the backend for a signed temporary upload URL, upload the screenplay to it, and
 * return the storage path to send as documents[0].file_path.
 */
export const uploadScreenplayViaSignedUrl = async (
  axios: AxiosInstance,
  uploadUrlEndpoint: string,
  file: File,
  onUploadProgress?: (fraction: number) => void
): Promise<string> => {
  const body = await axios.get(uploadUrlEndpoint);
  const { url, file_path } = readSignedUploadTarget(body);
  await putFileToSignedUrl(url, file, file.type || "application/octet-stream", onUploadProgress);
  return file_path;
};
